import type { BankTransferDetailView } from '@repo/api-contracts';
import { type ReactElement, useRef, useState } from 'react';
import { useCheckBusinessNumber } from '@/entities/user';
import { useModalDialog } from '@/shared/lib';
import { Button, CheckboxField, DialogFrame, FormField } from '@/shared/ui';
import {
  type BankTransferFormErrors,
  type BankTransferFormValues,
  parseBankTransferForm,
} from '../model/bank-transfer-form';
import { bankTransferApiErrorMessage } from '../model/bank-transfer-message';
import { useUpdateBankTransfer } from '../model/use-update-bank-transfer';
import { TaxInvoiceFields } from './tax-invoice-fields';
export const BankTransferEditDialog = ({
  orderId,
  transfer,
  onClose,
}: {
  readonly orderId: string;
  readonly transfer: BankTransferDetailView;
  readonly onClose: () => void;
}): ReactElement => {
  const snapshot = transfer.taxInvoiceRequest;
  const [values, setValues] = useState<BankTransferFormValues>(() => ({
    orderId,
    depositorName: transfer.depositorName,
    requestTaxInvoice: snapshot !== null,
    saveToProfile: false,
    businessNumber: snapshot?.businessNumber ?? '',
    companyName: snapshot?.companyName ?? '',
    representativeName: snapshot?.representativeName ?? '',
    address: snapshot?.address ?? '',
    businessType: snapshot?.businessType ?? '',
    businessItem: snapshot?.businessItem ?? '',
    invoiceEmail: snapshot?.invoiceEmail ?? '',
    contactName: snapshot?.contactName ?? '',
  }));
  const [errors, setErrors] = useState<BankTransferFormErrors>({});
  const [failure, setFailure] = useState<string | null>(null);
  const update = useUpdateBankTransfer();
  const lock = useRef(false);
  const business = useCheckBusinessNumber(values.businessNumber);
  const modal = useModalDialog(onClose, !update.isPending);
  const blocked = values.requestTaxInvoice && (business.state === 'CLOSED' || business.isPending);
  const change = <K extends keyof BankTransferFormValues>(
    key: K,
    value: BankTransferFormValues[K],
  ) => {
    setValues((previous) => ({ ...previous, [key]: value }));
    setErrors((previous) => ({ ...previous, [key]: undefined }));
  };
  const submit = async () => {
    if (lock.current || blocked) return;
    const parsed = parseBankTransferForm(values);
    if (!parsed.ok) {
      setErrors(parsed.errors);
      return;
    }
    lock.current = true;
    setFailure(null);
    try {
      await update.update(parsed.input);
      onClose();
    } catch (error) {
      setFailure(bankTransferApiErrorMessage(error));
    } finally {
      lock.current = false;
    }
  };
  return (
    <DialogFrame
      modal={modal}
      noValidate
      title="입금자명·세금계산서 정보 수정"
      onSubmit={(event) => {
        event.preventDefault();
        void submit();
      }}
      footer={
        <>
          <Button type="submit" disabled={blocked || update.isPending}>
            저장
          </Button>
          <Button type="button" variant="ghost" disabled={update.isPending} onClick={onClose}>
            닫기
          </Button>
        </>
      }
    >
      <fieldset disabled={update.isPending} className="flex flex-col gap-5">
        <FormField
          label="입금자명"
          value={values.depositorName}
          error={errors.depositorName}
          onChange={(event) => change('depositorName', event.target.value)}
        />
        <CheckboxField
          label="세금계산서 발행 요청"
          checked={values.requestTaxInvoice}
          onChange={(event) => change('requestTaxInvoice', event.target.checked)}
        />
        {values.requestTaxInvoice ? (
          <TaxInvoiceFields
            values={values}
            errors={errors}
            onChange={change}
            onCheck={() => {
              void business.check();
            }}
            businessState={business.state}
            isChecking={business.isPending}
          />
        ) : null}
      </fieldset>
      {failure ? <p role="alert">{failure}</p> : null}
    </DialogFrame>
  );
};
